import React, { useState } from 'react';
import { Switch, FormControlLabel, IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import type { MouseEvent } from 'react';
import {
  Visibility as VisibilityIcon,
  AccessTime as AccessTimeIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Route as RouteIcon,
  FilterList as FilterListIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Shift, ShiftStatus } from '../types';
import { formatTime, getShiftDurationHours, formatDate, isWithinShiftTimeWindow } from '../utils';
import { Badge } from '../../../pages/ShiftManagement/components/ui/Badge';

const shouldShowTooltip = (text: string | undefined | null): boolean => {
  return text ? text.trim().length > 0 : false;
};

interface ShiftCardProps {
  shift: Shift;
  onToggle: (shiftId: string) => void;
  onViewDetails: (shiftId: string) => void;
  onEdit?: (shiftId: string) => void;
  getStatusColor: (status: ShiftStatus) => string;
  isShiftEnabled: (shift: Shift) => boolean;
}

export const ShiftCard: React.FC<ShiftCardProps> = ({
  shift,
  onToggle,
  onViewDetails,
  onEdit,
  getStatusColor,
  isShiftEnabled,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    handleClose();
    onViewDetails(shift.id);
  };

  const handleEdit = () => {
    handleClose();
    if (onEdit) {
      onEdit(shift.id);
    }
  };

  const getStatusBadgeVariant = (status: ShiftStatus): 'success' | 'secondary' | 'warning' | 'destructive' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="group rounded-lg border border-gray-300 shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.1)] transition-all duration-300 relative overflow-hidden">
      <div className="p-5">
        {/* Shift Name */}
        <div className="flex items-start justify-between mb-1.5">
          {shouldShowTooltip(shift.name) ? (
            <Tooltip
              title={shift.name}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontSize: '0.75rem',
                    maxWidth: '200px',
                    padding: '8px 12px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  }
                },
                arrow: {
                  sx: {
                    color: 'white',
                  }
                }
              }}
            >
              <h3 className="text-sm font-bold text-gray-900 truncate leading-tight flex-1 pr-2 cursor-help min-w-0">
                <span className="text-xs font-mono text-gray-500 font-normal">ID: {shift.id}</span>
                <span className="mx-2 text-orange-500">|</span>
                {shift.name}
              </h3>
            </Tooltip>
          ) : (
            <h3 className="text-sm font-bold text-gray-900 truncate leading-tight flex-1 pr-2 min-w-0">
              <span className="text-xs font-mono text-gray-500 font-normal">ID: {shift.id}</span>
              <span className="mx-2 text-orange-500">|</span>
              {shift.name}
            </h3>
          )}
          {/* Actions Menu */}
          <IconButton
            size="small"
            onClick={handleClick}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            title="More options"
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              sx: {
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                minWidth: '150px',
                mt: 0.5,
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }
            }}
          >
            <MenuItem
              onClick={handleView}
              sx={{
                fontSize: '13px',
                py: 1.25,
                '&:hover': {
                  backgroundColor: 'rgba(59, 130, 246, 0.06)',
                }
              }}
            >
              <VisibilityIcon fontSize="small" sx={{ mr: 1.5, color: '#3B82F6', fontSize: '16px' }} />
              View Details
            </MenuItem>
            {onEdit && shift.status === 'draft' && (
              <MenuItem
                onClick={handleEdit}
                sx={{
                  fontSize: '13px',
                  py: 1.25,
                  '&:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.06)',
                  }
                }}
              >
                <EditIcon fontSize="small" sx={{ mr: 1.5, color: '#3B82F6', fontSize: '16px' }} />
                Edit
              </MenuItem>
            )}
          </Menu>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 mb-2">
          <Badge variant={getStatusBadgeVariant(shift.status)}>
            {shift.status.charAt(0).toUpperCase() + shift.status.slice(1)}
          </Badge>
        </div>

        {/* Content Section */}
        <div className="space-y-2">
          {/* Shift Configuration Section */}
          <div className="bg-blue-50/40 rounded-md p-2 border border-blue-100/60">
            <div className="flex items-center gap-1.5 mb-1.5">
              <RouteIcon style={{ fontSize: '14px', color: '#3B82F6' }} />
              <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wide">
                Shift Configuration
              </span>
            </div>
            <div className="text-xs">
              <div className="flex items-center gap-3 flex-wrap mb-1.5">
                <AccessTimeIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                <span className="font-semibold text-gray-700">Time:</span>
                <span className="text-gray-800 font-medium">
                  {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                </span>
              </div>
              {shift.startTime && (
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <AccessTimeIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                  <span className="font-semibold text-gray-700">Date:</span>
                  <span className="text-gray-800 font-medium">
                    {formatDate(shift.startTime)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Shift Details Section */}
          <div className="bg-purple-50/40 rounded-md p-2 border border-purple-100/60">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <FilterListIcon style={{ fontSize: '14px', color: '#9333EA' }} />
                <span className="text-[10px] font-bold text-purple-700 uppercase tracking-wide">Shift Details</span>
              </div>
            </div>

            <div className="mt-1.5 space-y-1.5">
              <div className="flex flex-wrap items-center gap-1.5 text-xs bg-white px-2 py-1.5 rounded-md shadow-sm border border-gray-100">
                <PeopleIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                <span className="font-bold text-gray-900">Required Drivers:</span>
                <span className="font-semibold text-primaryColor">{shift.requiredDrivers || shift.maxDrivers || 0}</span>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 text-xs bg-white px-2 py-1.5 rounded-md shadow-sm border border-gray-100">
                <AccessTimeIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                <span className="font-bold text-gray-900">Duration:</span>
                <span className="font-semibold text-primaryColor">{getShiftDurationHours(shift).toFixed(1)}h</span>
              </div>

              {shift.zone && (
                <div className="flex flex-wrap items-center gap-1.5 text-xs bg-white px-2 py-1.5 rounded-md shadow-sm border border-gray-100">
                  <LocationIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                  <span className="font-bold text-gray-900">Zone:</span>
                  <span className="font-semibold text-primaryColor">{shift.zone}</span>
                </div>
              )}

              {shift.shiftTemplate && (
                <div className="flex flex-wrap items-center gap-1.5 text-xs bg-white px-2 py-1.5 rounded-md shadow-sm border border-gray-100">
                  <RouteIcon style={{ fontSize: '12px', color: '#6B7280' }} />
                  <span className="font-bold text-gray-900">Shift Session:</span>
                  <span className="font-semibold text-primaryColor">{shift.shiftTemplate.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Toggle Switch - Only show within shift time window */}
        {isWithinShiftTimeWindow(shift) && (
          <div className="pt-3 border-t border-gray-200 mt-3">
            <FormControlLabel
              control={
                <Switch
                  checked={isShiftEnabled(shift)}
                  onChange={() => onToggle(shift.id)}
                  color="primary"
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#10B981',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#10B981',
                    },
                  }}
                />
              }
              label={
                <span className="text-xs font-medium text-gray-700">
                  {isShiftEnabled(shift) ? 'Active' : 'Inactive'}
                </span>
              }
              sx={{
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  marginLeft: '8px',
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

